import * as THREE from 'three';

// Module-level scratch objects: pointer-picking runs on high-frequency paths,
// so the NDC vector must not be re-allocated per event.
const _pointerNdc = new THREE.Vector2();

/**
 * Recursively intersect visible subtrees only.
 *
 * three's stock traversal still descends into (and raycasts) meshes whose
 * parents are invisible — every hidden helper layer in the scene therefore
 * paid triangle-test cost on each pointer event before its hits were thrown
 * away by the callers. Pruning at `!visible` removes those subtrees from the
 * traversal entirely.
 */
function intersectVisible(
    object: THREE.Object3D,
    raycaster: THREE.Raycaster,
    intersects: THREE.Intersection[]
): void {
    if (!object.visible) return;
    if (object.layers.test(raycaster.layers)) {
        object.raycast(raycaster, intersects);
    }
    const children = object.children;
    for (let i = 0; i < children.length; i++) {
        intersectVisible(children[i], raycaster, intersects);
    }
}

/**
 * Raycast the whole scene from a native pointer position.
 *
 * R3F only raycasts objects that carry event handlers, and BIM element meshes
 * have none — pointer events arrive via the invisible event-catching plane,
 * so event.intersections never contains BIM elements. Manual scene raycasting
 * is the single reliable "what is under the cursor" pick path. The ray is
 * recomputed from the native pointer coordinates because an event's own ray
 * can be stale on the first event after activation.
 *
 * Heavy meshes get BVH acceleration through the global three-mesh-bvh patch
 * (see rendering/instancing/instancingUtils.ts: accelerated Mesh.raycast +
 * computed bounds trees on bulk geometry); everything else falls back to the
 * built-in bounding-sphere rejection.
 */
export function raycastSceneAtPointer(
    raycaster: THREE.Raycaster,
    camera: THREE.Camera,
    scene: THREE.Scene,
    domElement: HTMLCanvasElement,
    clientX: number,
    clientY: number
): THREE.Intersection[] {
    const rect = domElement.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return [];

    _pointerNdc.set(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1
    );
    camera.updateMatrixWorld();
    raycaster.setFromCamera(_pointerNdc, camera);

    const intersects: THREE.Intersection[] = [];
    const children = scene.children;
    for (let i = 0; i < children.length; i++) {
        intersectVisible(children[i], raycaster, intersects);
    }
    intersects.sort((a, b) => a.distance - b.distance);
    return intersects;
}

/**
 * Walk up the parent chain of a hit object and return the first userData.id
 * that maps to an element in the given id set (or a levelId fallback).
 */
export function resolveElementIdFromObject(
    object: THREE.Object3D,
    validIds: Set<string> | ((id: string) => boolean)
): string | null {
    const isValid = typeof validIds === 'function' ? validIds : (id: string) => validIds.has(id);
    let current: THREE.Object3D | null = object;
    while (current) {
        if (current.userData?.id) {
            const id = current.userData.id as string;
            if (isValid(id)) return id;
            if (current.userData.levelId) return current.userData.levelId as string;
        }
        current = current.parent;
    }
    return null;
}
